import { useRef, useCallback, useMemo, useEffect } from 'react';
import { useMatch, generatePath, useNavigate } from 'react-router-dom';
import { useMemoizedFn } from 'ahooks';
import useSupplierStore from '@/stores/supplier-session';
import useUserStore from '@/stores/userStore';
import type { VirtualListInstance } from '@/common/base/virtual-scroll-list';
import { PagePath } from '@/routes/paths';
import SiderMenuEvent from '@/events/sider-menu';
import type { IApiSourcingContactEmail } from '@/services/swagger/easourcing/models';
import { formatMessageListTime } from '@/utils/dayjs';
import NotLoggedInIllustration from '@/assets/icons/message-list-not-logged-in.svg?react';
import { PushEventType, usePushMessage } from '@/hooks/useWebSocket';
import { getContactEmailGet } from '@/services/swagger/easourcing/ContactService';
import ContactListView from './ContactListView';

// ─── ViewModel ────────────────────────────────────────────────────────────────

export type MessageListItemViewModel = {
  id: string;
  type: 'NORMAL' | 'FACTORY' | 'FACTORY_GZH';
  displayName: string;
  hasShieldCheck: boolean;
  timeDisplay: string;
  latestPreview: string;
  isAiManaged: boolean;
  autoReplyEnabled: boolean;
  lastMsgContent: string;
  lastMsgTime: string;
  companyImageUrl?: string;
  rawUpdatedAt: string;
  sortType: 'PINNED_TOP' | 'NORMAL';
};

export function toViewModel(contact: IApiSourcingContactEmail): MessageListItemViewModel {
  const info = contact.emailInfoList?.[0];
  const displayName = info?.name || contact.email || 'Unknown';
  const autoReplyEnabled = contact.autoReplyEnabled === 1;
  const lastMsgTime = contact.lastMsgTime ?? '';
  const lastMsgContent = contact.lastMsgContent ?? '';
  const timeDisplay = formatMessageListTime(lastMsgTime || contact.updatedAt);
  return {
    id: contact.id,
    type: contact.type as MessageListItemViewModel['type'],
    displayName,
    hasShieldCheck: contact.type === 'FACTORY',
    timeDisplay,
    latestPreview: lastMsgContent || 'Awaiting response...',
    isAiManaged: autoReplyEnabled,
    autoReplyEnabled,
    lastMsgContent,
    lastMsgTime,
    companyImageUrl: undefined,
    rawUpdatedAt: contact.updatedAt,
    sortType: contact.sortType,
  };
}

// ─── useMessageListData ───────────────────────────────────────────────────────

/**
 * 管理消息列表的数据初始化、滚动定位与事件监听：
 * - 用户登录后重载列表并滚动到当前联系人
 * - 监听全局 autoFocusCurrentSupplier 事件触发滚动定位
 * - 路由切换时滚动到当前联系人
 */
function useMessageListData(
  viewModels: MessageListItemViewModel[],
  activeContactId: string | null,
  virtualListRef: React.RefObject<VirtualListInstance>
) {
  const reloadSupplierList = useSupplierStore(state => state.reloadSupplierList);
  const { user } = useUserStore();

  const scrollToCurrent = useMemoizedFn(() => {
    if (!activeContactId) return;
    const index = viewModels.findIndex(item => item.id === activeContactId);
    if (index > -1) {
      virtualListRef.current?.scrollToIndex(index, { behavior: 'smooth', align: 'center' });
    }
  });

  // 用户登录后初始化列表，加载完成后滚动到当前联系人；同时初始化全局自动回复开关
  useEffect(() => {
    if (user?.userId) {
      reloadSupplierList().then(() => {
        scrollToCurrent();
      });
    }
  }, [user?.userId, reloadSupplierList, scrollToCurrent]);

  // 监听全局事件，自动聚焦当前联系人
  useEffect(() => {
    const handleAutoFocus = () => {
      scrollToCurrent();
    };
    SiderMenuEvent.on('autoFocusCurrentSupplier', handleAutoFocus);
    return () => {
      SiderMenuEvent.off('autoFocusCurrentSupplier', handleAutoFocus);
    };
  }, [scrollToCurrent]);

  // 路由切换时滚动到当前联系人
  useEffect(() => {
    if (activeContactId) {
      scrollToCurrent();
    }
  }, [activeContactId, scrollToCurrent]);
}

// ─── ContactListNotLoggedIn（未登录缺省态）────────────────────────────────────

function ContactListNotLoggedIn() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-[10px] px-6">
      <NotLoggedInIllustration className="w-[100px] h-[100px] shrink-0" />
      <div className="flex flex-col items-center gap-2">
        <p className="text-sm text-[#737373] leading-[150%] text-center">No session yet</p>
      </div>
    </div>
  );
}

// ─── ContactListMenu（逻辑组件）─────────────────────────────────────────────

export function ContactListMenu() {
  const { user } = useUserStore();
  const supplierList = useSupplierStore(state => state.supplierList);
  const unreadStatusMap = useSupplierStore(state => state.unreadStatusMap);
  const scrollOffset = useSupplierStore(state => state.scrollOffset);
  const setScrollOffset = useSupplierStore(state => state.setScrollOffset);
  const loadSupplierList = useSupplierStore(state => state.loadSupplierList);
  const togglePin = useSupplierStore(state => state.togglePin);
  const updateSupplierItem = useSupplierStore(state => state.updateSupplierItem);

  const virtualListRef = useRef<VirtualListInstance>(null);
  const navigate = useNavigate();
  const matchResult = useMatch(PagePath.supplierDetail);
  const activeContactId = matchResult?.params?.contactId ?? null;

  const viewModels = useMemo(() => supplierList.map(toViewModel), [supplierList]);

  const handleNavigate = useMemoizedFn((contactId: string) => {
    navigate(generatePath(PagePath.supplierDetail, { contactId }));
  });

  const handleBottomReached = useCallback(() => {
    loadSupplierList();
  }, [loadSupplierList]);

  const handleTogglePin = useMemoizedFn((contactId: string, isPinned: boolean) => {
    return togglePin(contactId, isPinned);
  });

  // 监听联系人最新消息变更推送事件，更新侧边栏联系人的最新消息时间等信息
  usePushMessage<{ userId: string; contactId: string }>(PushEventType.CONTACT_LAST_CHANGE, message => {
    const { contactId: pushContactId } = message.content ?? {};

    if (pushContactId && supplierList.some(item => item.id === pushContactId)) {
      getContactEmailGet({ contactId: pushContactId })
        .then(data => {
          if (data) {
            updateSupplierItem(pushContactId, {
              lastMsgTime: data.lastMsgTime,
            });
          }
        })
        .catch(() => {
          // 更新联系人最新消息时间失败，忽略，不影响主流程
        });
    }
  });

  useMessageListData(viewModels, activeContactId, virtualListRef);

  if (!user?.userId) {
    return <ContactListNotLoggedIn />;
  }

  return (
    <ContactListView
      viewModels={viewModels}
      activeContactId={activeContactId}
      unreadStatusMap={unreadStatusMap}
      scrollOffset={scrollOffset}
      virtualListRef={virtualListRef}
      onNavigate={handleNavigate}
      onBottomReached={handleBottomReached}
      onScrollOffsetChange={setScrollOffset}
      onTogglePin={handleTogglePin}
    />
  );
}
